import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Mail, Phone, Calendar, CheckCircle2 } from 'lucide-react';

interface OnboardingFlowProps {
  onNavigate: (screen: string) => void;
}

export function OnboardingFlow({ onNavigate }: OnboardingFlowProps) {
  const [step, setStep] = useState<'contact' | 'birthdate' | 'magiclink-sent'>('contact');
  const [contactMethod, setContactMethod] = useState('');

  const handleContactSubmit = () => {
    // Simulate checking if user is registered
    const registered = Math.random() > 0.5;
    
    if (registered) {
      setStep('magiclink-sent');
    } else {
      setStep('birthdate');
    }
  };

  const handleBirthdateSubmit = () => {
    setStep('magiclink-sent');
  };

  const handleMagiclinkClick = () => {
    onNavigate('dashboard');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-secondary/20 to-accent/20">
      <Card className="w-full max-w-md p-8 shadow-xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl mb-2 text-primary">Welcome to BtGtR</h1>
          <p className="text-sm text-muted-foreground">Enter your details to get started</p>
        </div>

        {/* Contact Entry Step */}
        {step === 'contact' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="contact">Email or Phone Number</Label>
              <div className="relative">
                <Input
                  id="contact"
                  placeholder="email@example.com or +1234567890"
                  value={contactMethod}
                  onChange={(e) => setContactMethod(e.target.value)}
                  className="pl-10"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {contactMethod.includes('@') ? (
                    <Mail className="w-4 h-4" />
                  ) : (
                    <Phone className="w-4 h-4" />
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">We'll send you a magic link to log in securely</p>
            </div>

            <Button
              onClick={handleContactSubmit}
              className="w-full bg-primary hover:bg-primary/90"
              disabled={!contactMethod}
            >
              Continue
            </Button>
          </div>
        )}

        {/* Birthdate Entry Step (for new users) */}
        {step === 'birthdate' && (
          <div className="space-y-6">
            <div className="p-4 bg-accent/50 rounded-lg border border-primary/10">
              <p className="text-sm">
                <strong>New user detected!</strong> Please enter your birthdate to complete registration.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthdate">Your Birthdate</Label>
              <div className="relative">
                <Input
                  id="birthdate"
                  type="text"
                  placeholder="MM/DD"
                  className="pl-10"
                  maxLength={5}
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                We use this to celebrate your birthday with gift groups (year not required)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-contact">Confirm Contact</Label>
              <Input
                id="confirm-contact"
                value={contactMethod}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep('contact')}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleBirthdateSubmit}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                Create Account
              </Button>
            </div>
          </div>
        )}

        {/* Magic Link Sent Confirmation */}
        {step === 'magiclink-sent' && (
          <div className="space-y-6 text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>

            <div>
              <h2 className="text-2xl mb-2">Check Your {contactMethod.includes('@') ? 'Email' : 'Phone'}</h2>
              <p className="text-sm text-muted-foreground">
                We've sent a magic link to <strong className="text-foreground">{contactMethod}</strong>
              </p>
            </div>

            <div className="p-4 bg-accent/50 rounded-lg border border-primary/10 text-left">
              <p className="text-sm mb-2">
                <strong>What's a magic link?</strong>
              </p>
              <p className="text-xs text-muted-foreground">
                Click the link in your {contactMethod.includes('@') ? 'email' : 'text message'} to securely log in. 
                No password required!
              </p>
            </div>

            <Button
              variant="outline"
              onClick={handleMagiclinkClick}
              className="w-full border-primary hover:bg-primary/5"
            >
              Demo: Simulate Magic Link Click
            </Button>

            <Button
              variant="ghost"
              onClick={() => setStep('contact')}
              className="w-full text-muted-foreground"
              size="sm"
            >
              Use a different contact method
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
